// src/screens/LandingPage.js - UPDATED WITH CORRECT NAVIGATION STATE
import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import AdSlot from '../components/AdSlot';
import AppStoreLinks from '../components/AppStoreLinks';

/**
 * Checks if a bonus campaign is currently active and not expired.
 * This logic is now compatible with the date-only format from the admin panel.
 * @param {object} campaign The campaign object from Firestore.
 * @returns {boolean} True if the campaign should be displayed, false otherwise.
 */
const isBonusCampaignEffectivelyActive = (campaign) => {
    // A campaign must be explicitly set to active.
    if (!campaign.isActive) {
        return false;
    }

    // Check if the campaign has a valid expiration date.
    if (campaign.expiresAt) {
        // Get today's date as a string in 'YYYY-MM-DD' format.
        // This avoids timezone issues by comparing strings directly.
        const today = new Date().toISOString().split('T')[0];

        // If the campaign's expiration date is before today, it's expired.
        if (campaign.expiresAt < today) {
            return false;
        }
    }

    // If the campaign is active and hasn't expired, it's valid.
    return true;
};

export default function LandingPage() {
    const navigate = useNavigate();

    const [activeCampaign, setActiveCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [campaignUrgency, setCampaignUrgency] = useState(null);

    // This effect listens for active bonus campaigns in real-time.
    useEffect(() => {
        if (!db) return;

        // Query for campaigns marked as active in the database.
        const q = query(collection(db, "bonus_campaigns"), where("isActive", "==", true));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const allActiveCampaigns = snapshot.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data() 
                }));

                // Filter campaigns that are TRULY active (not expired).
                const effectivelyActiveCampaigns = allActiveCampaigns.filter(isBonusCampaignEffectivelyActive);

                if (effectivelyActiveCampaigns.length > 0) {
                    // Sort to show the most urgent campaign first (the one expiring soonest).
                    const sortedCampaigns = effectivelyActiveCampaigns.sort((a, b) => {
                        if (a.expiresAt && !b.expiresAt) return -1;
                        if (!a.expiresAt && b.expiresAt) return 1;
                        if (a.expiresAt && b.expiresAt) {
                            return new Date(a.expiresAt) - new Date(b.expiresAt);
                        }
                        if (a.createdAt && b.createdAt) {
                            return b.createdAt.toDate() - a.createdAt.toDate();
                        }
                        return 0;
                    });

                    const selectedCampaign = sortedCampaigns[0];
                    setActiveCampaign(selectedCampaign);

                    // Calculate and set urgency message if the campaign expires soon.
                    if (selectedCampaign.expiresAt) {
                        const expiresDateTime = new Date(selectedCampaign.expiresAt + 'T23:59:59');
                        const now = new Date();
                        const diffTime = expiresDateTime - now;
                        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        
                        if (diffHours <= 168) { // Less than or equal to 7 days
                            let message;
                            let urgencyLevel;
                            
                            if (diffHours <= 1) {
                                message = 'Última chance! Encerraremos em menos de 1 hora!';
                                urgencyLevel = 'critical';
                            } else if (diffHours <= 24) {
                                message = `Não perca. Termina em ${diffHours} horas!`;
                                urgencyLevel = 'high';
                            } else {
                                message = `Corra, restam ${diffDays} dias.`;
                                urgencyLevel = 'medium';
                            }
                            
                            setCampaignUrgency({
                                message,
                                urgencyLevel,
                                expiresAt: expiresDateTime.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                            });
                        } else {
                            setCampaignUrgency(null);
                        }
                    } else {
                        setCampaignUrgency(null);
                    }
                } else {
                    setActiveCampaign(null);
                    setCampaignUrgency(null);
                }
            } else {
                setActiveCampaign(null);
                setCampaignUrgency(null);
            }
            setIsLoading(false);
        });

        return () => unsubscribe(); // Cleanup the listener
    }, []);

    // --- DYNAMIC CONTENT ---
    const headline = "A Nova Forma de Trocar.";
    const subHeadline = "Transforme o que você não usa em coisas que você quer. Uma comunidade de troca baseada em créditos, sem precisar de dinheiro.";
    
    let buttonText = "Quero Entrar e Ajudar";
    if (activeCampaign) {
        buttonText = `Criar Conta e Ganhar ${activeCampaign.creditAmount} Créditos`;
    }

    // ✅ HELPER: Correctly navigate with state
    const handleSignUpClick = () => {
        if (activeCampaign) {
            // Use { state: { ... } } so useLocation().state works in SignUpScreen
            navigate('/signup', { state: { activeCampaign } });
        } else {
            navigate('/signup');
        }
    };

    return (
        <div className="bg-gray-50">
            <main>
                <section className="py-20 md:py-24" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7, #bbf7d0)' }}>
                    <div className="container mx-auto px-6 text-center">
                        {/* Campaign Urgency Banner */}
                        {activeCampaign && campaignUrgency && (
                            <div className={`mb-6 p-4 rounded-lg mx-auto max-w-2xl ${
                                campaignUrgency.urgencyLevel === 'critical' 
                                    ? 'bg-red-100 border-2 border-red-300 text-red-800 animate-pulse' 
                                    : campaignUrgency.urgencyLevel === 'high'
                                    ? 'bg-red-100 border border-red-300 text-red-800'
                                    : 'bg-orange-100 border border-orange-300 text-orange-800'
                            }`}>
                                <div className="flex items-center justify-center">
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div className="text-center">
                                        <span className="font-bold block">{campaignUrgency.message}</span>
                                        <span className="text-xs opacity-75">Expira em: {campaignUrgency.expiresAt}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Campaign Highlight */}
                        {activeCampaign && (
                            <div 
                                onClick={handleSignUpClick}
                                className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6 mb-8 mx-auto max-w-md border-l-4 border-green-500 cursor-pointer hover:shadow-xl transition"
                            >
                                <h3 className="text-xl font-bold text-green-600 mb-2">
                                    {'\u{1F389}'} {activeCampaign.name}
                                </h3>
                                <p className="text-lg text-gray-700 mb-2">
                                    Cadastre-se e ganhe <span className="font-bold text-green-600">{activeCampaign.creditAmount} créditos</span> grátis!
                                </p>
                                <p className="text-lg text-gray-700 mb-2">
                                    Para utilizar por tempo determinado
                                </p>
                            </div>
                        )}

                        <h1 className="text-4xl md:text-5xl font-black text-green-900 leading-tight">{headline}</h1>
                        <p className="mt-4 text-lg md:text-xl text-green-800 max-w-3xl mx-auto">
                            {subHeadline}
                        </p>
                        <div className="mt-8">
                            <button 
                                onClick={handleSignUpClick}
                                className={`px-10 py-4 rounded-lg text-lg font-semibold shadow-lg transition transform hover:scale-105 ${
                                    activeCampaign 
                                        ? 'bg-green-600 text-white hover:bg-green-700' 
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                } ${campaignUrgency?.urgencyLevel === 'critical' ? 'animate-pulse' : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? "Carregando..." : buttonText}
                            </button>
                        </div>
                    </div>
                </section>

                <div className="container mx-auto px-6 pt-10">
                    <AdSlot placement="landing_banner" />
                </div>
                
                <section className="py-20">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">É simples, rápido e justo</h2>
                            <p className="text-gray-600 mt-2">Comece a trocar em 4 passos.</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">1</div>
                                <h3 className="text-lg font-semibold text-gray-800 mt-5">Cadastre-se</h3>
                                <p className="text-gray-500 mt-2 text-sm">Crie sua conta grátis e junte-se à comunidade.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">2</div>
                                <h3 className="text-lg font-semibold text-gray-800 mt-5">Ganhe Créditos</h3>
                                <p className="text-gray-500 mt-2 text-sm">Anuncie seus itens, entregue e ganhe créditos.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">3</div>
                                <h3 className="text-lg font-semibold text-gray-800 mt-5">Encontre o que Ama</h3>
                                <p className="text-gray-500 mt-2 text-sm">Navegue por milhares de produtos.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">4</div>
                                <h3 className="text-lg font-semibold text-gray-800 mt-5">Troque com Créditos</h3>
                                <p className="text-gray-500 mt-2 text-sm">Adquira itens de forma segura e sem dinheiro.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <AppStoreLinks />
            </main>
        </div>
    );
}
